/**
 * Cash session and ticket numbering.
 */

import { formatTicket, newId, parseTicket, type Centavos } from '@abastece/shared';
import { dolaresEsperados, efectivoEsperado, responsableDe, type SesionCaja } from '@abastece/core';
import { db, getMeta } from '../db/dexie.js';
import { emitir, emitirYVer } from './emitir.js';
import { fondoEnLibros, ultimoRelevo, type Relevo } from './fondo.js';
import { traducir, type Clave } from '../i18n/index.js';

export type { Relevo } from './fondo.js';

export async function sesionAbierta(): Promise<SesionCaja | undefined> {
  const dispositivoId = await getMeta('dispositivoId');
  if (!dispositivoId) return undefined;
  return db.sesiones
    .filter((s) => s.dispositivoId === dispositivoId && s.estado === 'abierta')
    .first();
}

/** A till open now, with whose drawer it is (`responsableDe`) and the device it is open on. */
export interface CajaAbierta {
  sesion: SesionCaja;
  quien: string;
  equipo: string;
  /** Open on this device. */
  aqui: boolean;
}

/**
 * Every till open now, on any device: this device's first, then the others by when they opened.
 * The owner pays an expense out of any of them in «Gastos» (2026-10-16: «We need an option to be
 * able to pay an expense from the till»), from a phone with no till of its own: the books take any
 * till that is open and holds enough (`pagoDeCaja`), and they never asked whose.
 */
export async function cajasAbiertas(): Promise<CajaAbierta[]> {
  const [dispositivoId, abiertas, usuarios, equipos] = await Promise.all([
    getMeta('dispositivoId'),
    db.sesiones.where('estado').equals('abierta').toArray(),
    db.usuarios.toArray(),
    db.dispositivos.toArray(),
  ]);
  const nombre = new Map(usuarios.map((u) => [u.id, u.nombre]));
  const equipo = new Map(equipos.map((d) => [d.id, d.nombre]));
  return abiertas
    .map((s) => ({ sesion: s, quien: nombre.get(responsableDe(s)) ?? '', equipo: equipo.get(s.dispositivoId) ?? '', aqui: s.dispositivoId === dispositivoId }))
    .sort((a, b) => Number(b.aqui) - Number(a.aqui) || a.sesion.abiertaEn.localeCompare(b.sesion.abiertaEn));
}

/** One line of how a drawer's expected cash is reached: which way it goes, what it is, how much. */
export interface PasoDelEsperado {
  signo?: '+' | '−' | '=';
  clave: Clave;
  monto: Centavos;
}

/** A line that adds or takes away, or none when there is nothing on it. A difference goes either way. */
function paso(clave: Clave, monto: number | undefined, signo: '+' | '−'): PasoDelEsperado[] {
  if (!monto) return [];
  if (monto < 0) return [{ signo: signo === '+' ? '−' : '+', clave, monto: -monto as Centavos }];
  return [{ signo, clave, monto: monto as Centavos }];
}

/**
 * How the córdobas a drawer should hold are reached, a line each as on a receipt: the float it
 * opened with, then each thing that put cash in or took it out, then what that comes to. It is
 * `efectivoEsperado`, line by line, in the words «Caja» shows the open till in, so the corte's
 * «Esperado en córdobas» can say what it is made of once the drawer has been counted. A line with
 * nothing on it is left out; the float and the total never are.
 */
export function pasosDelEsperado(s: SesionCaja): PasoDelEsperado[] {
  return [
    { clave: 'caja.fondoInicial', monto: s.fondoInicial },
    ...paso('caja.efectivoRecibido', s.efectivoCordobas, '+'),
    ...paso('caja.vueltoEntregado', s.cambioEntregado, '−'),
    ...paso('caja.retiros', s.retiros, '−'),
    ...paso('caja.ingresos', s.ingresos, '+'),
    ...paso('caja.abonos', s.abonos, '+'),
    ...paso('caja.devoluciones', s.devoluciones, '−'),
    ...paso('caja.pagadoDeGaveta', s.pagado, '−'),
    ...paso('caja.ajusteEntrega', s.ajusteCordobas, '+'),
    { signo: '=', clave: 'caja.esperadoCordobas', monto: efectivoEsperado(s) },
  ];
}

/** The dollars the same way (`dolaresEsperados`): change is given in córdobas, so nothing here is change. */
export function pasosDeLosDolares(s: SesionCaja): PasoDelEsperado[] {
  return [
    { clave: 'caja.fondoEnDolares', monto: (s.fondoDolares ?? 0) as Centavos },
    ...paso('caja.dolaresRecibidos', s.efectivoDolares, '+'),
    ...paso('caja.dolaresIngresados', s.ingresosDolares, '+'),
    ...paso('caja.dolaresRetirados', s.retirosDolares, '−'),
    ...paso('caja.ajusteEntregaDolares', s.ajusteDolares, '+'),
    { signo: '=', clave: 'caja.esperadoDolares', monto: dolaresEsperados(s) },
  ];
}

/**
 * The float the last corte on this device left at `ubicacion`, for the next opening to be held
 * against (see `ultimoRelevo`). None on a drawer with no float to hand on: its float comes from
 * general cash.
 */
export async function fondoQueQuedo(ubicacion: string): Promise<Relevo | null> {
  const dispositivoId = await getMeta('dispositivoId');
  if (!dispositivoId) return null;
  const cerradas = await db.sesiones.where('estado').equals('cerrada').toArray();
  return ultimoRelevo(cerradas, dispositivoId, ubicacion);
}

/**
 * Opens a shift. `fondoDolares` in USD cents: the dollars counted in the drawer, apart from the
 * córdobas.
 *
 * `enLibros` says where the float came from (see `caja.abierta`). The count is held against what
 * the last corte left, if it left a float, and `puesto`, the córdobas the opener says they added
 * from general cash (`fondoEnLibros`). With no float left and nobody asked (no `puesto`), the whole
 * count comes from general cash — always, on a truck, whose settlement hands everything back.
 * `primerConteo`: the córdobas of a first count that was off and was redone, for the owner to see
 * beside the second.
 */
export async function abrirCaja(
  fondoInicial: Centavos,
  ubicacion: string,
  fondoDolares: Centavos = 0 as Centavos,
  enLibros?: EnLibros,
): Promise<string> {
  const sesionId = newId();
  await emitir('caja.abierta', apertura(sesionId, fondoInicial, ubicacion, fondoDolares, enLibros));
  return sesionId;
}

/**
 * `abrirCaja`, and whether this device's books took it: the reducer's reason when they did not
 * (another till opened here meanwhile, in another tab, say). For a screen that must not say the
 * till is open when it is not.
 */
export async function abrirCajaYVer(
  fondoInicial: Centavos,
  ubicacion: string,
  fondoDolares: Centavos,
  enLibros: EnLibros,
): Promise<{ sesionId: string; rechazo: string | null }> {
  const sesionId = newId();
  const { rechazo } = await emitirYVer('caja.abierta', apertura(sesionId, fondoInicial, ubicacion, fondoDolares, enLibros));
  return { sesionId, rechazo };
}

type EnLibros = { relevo: Relevo | null; puesto?: Centavos; primerConteo?: Centavos };

function apertura(sesionId: string, fondoInicial: Centavos, ubicacion: string, fondoDolares: Centavos, enLibros?: EnLibros) {
  return {
    sesionId,
    ubicacion,
    fondoInicial,
    ...(fondoDolares > 0 ? { fondoDolares } : {}),
    ...(enLibros ? fondoEnLibros(fondoInicial, enLibros.relevo, enLibros.puesto) : {}),
    ...(enLibros?.primerConteo !== undefined ? { primerConteo: enLibros.primerConteo } : {}),
  };
}

/**
 * Next ticket number for this terminal.
 *
 * Derived from the highest ticket this device has issued rather than from a stored counter, so
 * a wiped or restored device picks up where it left off instead of re-issuing numbers that are
 * already on a customer's receipt. Tickets sort as "AB-000042", so the index range scan finds
 * the last one for this device without loading the rest.
 */
export async function siguienteTicket(): Promise<string> {
  const codigo = await getMeta('dispositivoCodigo');
  if (!codigo) throw new Error(traducir('error.sinCodigo'));

  const ultima = await db.ventas
    .where('ticket')
    .between(`${codigo}-`, `${codigo}-￿`)
    .reverse()
    .first();

  const previo = ultima ? parseTicket(ultima.ticket)?.seq ?? 0 : 0;
  return formatTicket(codigo, previo + 1);
}
